/**
 * AppShell — Authenticated app layout.
 *
 * Unified nav bar matching PublicLayout style:
 *   - Logo (links to /app) on left
 *   - Nav links: Home, Pricing, FAQ, Contact, Tribe Space (active)
 *   - Avatar dropdown on right: Profile, My Tribe, Settings, Support, Legal, Sign Out
 * Mobile: hamburger icon opens a slide-in drawer.
 *
 * Main content area renders child routes (the tab-based workflow).
 */

import React, { useState, useRef, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../common/Logo';
import TrialBanner from '../common/TrialBanner';
import MobileDrawer from './MobileDrawer';

import {
  ChevronDown, User, Users, Settings,
  HelpCircle, LogOut, FileText, Shield, Menu,
} from 'lucide-react';

const AppShell: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isPrimary = user?.role === 'primary';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNav = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleSignOut = () => {
    setMenuOpen(false);
    signOut();
    navigate('/');
  };

  // Determine active nav link
  const isAppRoute = location.pathname.startsWith('/app');

  return (
    <div className="app-shell">
      {/* ================================================================
         Unified Navigation Bar (matches PublicLayout style)
         ================================================================ */}
      <nav className="public-nav">
        <div className="nav-inner">
          <Link to="/app" className="nav-logo">
            <Logo size={75} />
          </Link>

          {/* Desktop nav links */}
          <div className="nav-links">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/pricing" className="nav-link">Pricing</Link>
            <Link to="/faq" className="nav-link">FAQ</Link>
            <Link to="/contact" className="nav-link">Contact</Link>
            <Link to="/app" className={`nav-link ${isAppRoute ? 'active' : ''}`}>Tribe Space</Link>

            {/* Avatar dropdown */}
            <div className="topbar-right" ref={menuRef}>
              <button
                className="topbar-user-btn"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-expanded={menuOpen}
                aria-haspopup="true"
              >
                <span className="topbar-avatar">
                  {user?.displayName?.charAt(0)?.toUpperCase() || 'U'}
                </span>
                <span className="topbar-user-name">{user?.displayName}</span>
                <ChevronDown size={16} className={`topbar-chevron ${menuOpen ? 'open' : ''}`} />
              </button>

              {menuOpen && (
                <div className="topbar-dropdown">
                  <div className="dropdown-header">
                    <span className="dropdown-name">{user?.displayName}</span>
                    <span className="dropdown-role">
                      {isPrimary ? 'Primary User' : 'Member'}
                    </span>
                  </div>
                  <div className="dropdown-divider" />

                  <button className="dropdown-item" onClick={() => handleNav('/app/profile')}>
                    <User size={16} /> Profile
                  </button>
                  <button className="dropdown-item" onClick={() => handleNav('/app/users')}>
                    <Users size={16} /> My Tribe
                  </button>
                  {isPrimary && (
                    <button className="dropdown-item" onClick={() => handleNav('/app/settings')}>
                      <Settings size={16} /> Settings
                    </button>
                  )}
                  <button className="dropdown-item" onClick={() => handleNav('/app/support')}>
                    <HelpCircle size={16} /> Support
                  </button>

                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => handleNav('/terms')}>
                    <FileText size={16} /> Terms of Service
                  </button>
                  <button className="dropdown-item" onClick={() => handleNav('/privacy')}>
                    <Shield size={16} /> Privacy Policy
                  </button>

                  {user?.isAdmin && (
                    <>
                      <div className="dropdown-divider" />
                      <button className="dropdown-item" onClick={() => handleNav('/admin')}>
                        <Settings size={16} /> Admin Dashboard
                      </button>
                    </>
                  )}

                  <div className="dropdown-divider" />
                  <button className="dropdown-item dropdown-item--danger" onClick={handleSignOut}>
                    <LogOut size={16} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile hamburger button */}
          <button
            className="nav-hamburger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Subscription status banner — trial countdown, expired notice, etc. */}
      <TrialBanner />

      {/* ================================================================
         Main Content Area (renders DashboardPage or dropdown pages)
         ================================================================ */}
      <main className="app-content">
        <Outlet />
      </main>
    </div>
  );
};

export default AppShell;
